import React, { useState } from 'react';
import { X, Copy, Check, Clock, File, Folder, ExternalLink } from 'lucide-react';
import { GenerateSignedLinkResponse } from '../../../shared/types';

interface SignedLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  signedLinkData: GenerateSignedLinkResponse | null;
  isLoading: boolean;
  error: string | null;
}

export const SignedLinkModal: React.FC<SignedLinkModalProps> = ({
  isOpen,
  onClose,
  signedLinkData,
  isLoading,
  error
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = async () => {
    if (!signedLinkData?.signedUrl) return;

    try {
      await navigator.clipboard.writeText(signedLinkData.signedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = signedLinkData.signedUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatExpirationTime = (expiresAt: string) => {
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const diffMs = expirationDate.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
    } else {
      return 'Less than 1 minute';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <ExternalLink className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Generate Signed Link
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Generating signed link...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {signedLinkData && (
            <div className="space-y-6">
              {/* Link Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  {signedLinkData.linkType === 'file' ? (
                    <File className="w-5 h-5 text-gray-600" />
                  ) : (
                    <Folder className="w-5 h-5 text-gray-600" />
                  )}
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {signedLinkData.linkType === 'file' ? 'File Link' : 'Folder Link'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {signedLinkData.linkType === 'file' 
                        ? signedLinkData.fileName
                        : `${signedLinkData.folderName} (${signedLinkData.fileCount} files)`
                      }
                    </p>
                  </div>
                </div>

                {/* Expiration Info */}
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>
                    Expires in {formatExpirationTime(signedLinkData.expiresAt)}
                  </span>
                </div>
              </div>

              {/* Generated Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signed Download Link
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={signedLinkData.signedUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                  />
                  <button
                    onClick={handleCopyToClipboard}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      copied
                        ? 'bg-green-100 text-green-800 border border-green-200'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 inline mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 inline mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Usage Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h4 className="font-medium text-blue-900 mb-2">How to use this link:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Share this link with anyone who needs access to the {signedLinkData.linkType}</li>
                  <li>• No login is required to download using this link</li>
                  <li>• The link will expire automatically after the time shown above</li>
                  <li>• For security, avoid sharing this link in unsecured channels</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          {signedLinkData && (
            <a
              href={signedLinkData.signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
            >
              Test Link
            </a>
          )}
        </div>
      </div>
    </div>
  );
};